import React, { useState } from 'react';
import { Plus, ArrowUpRight, ArrowDownRight, Upload, FileText } from 'lucide-react';
import { Transaction } from '../types';
import { formatCurrency } from '../utils/helpers';
import { mockTransactions } from '../utils/mockData';
import { BulkTransactionImport } from '../components/BulkTransactionImport';
import { DocumentUpload } from '../components/DocumentUpload';
import { AICategorySuggestion } from '../components/AICategorySuggestion';
import { AIDemo } from '../components/AIDemo';

export const TransactionsPage: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>(mockTransactions);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    type: 'expense' as 'income' | 'expense',
    category: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newTransaction: Transaction = {
      id: Date.now().toString(),
      user_id: 'user1',
      amount: formData.type === 'expense' ? -Math.abs(Number(formData.amount)) : Math.abs(Number(formData.amount)),
      type: formData.type,
      category: formData.category,
      description: formData.description,
      classification: 'personal',
      date: formData.date,
      currency: 'USD',
      created_at: new Date().toISOString(),
    };

    setTransactions([newTransaction, ...transactions]);
    setFormData({
      amount: '',
      type: 'expense',
      category: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
    });
    setShowAddForm(false);
  };

  const handleBulkTransactionsAdded = (newTransactions: Transaction[]) => {
    setTransactions([...newTransactions, ...transactions]);
  };

  const handleDocumentTransactionsAdded = (newTransactions: Transaction[]) => {
    setTransactions([...newTransactions, ...transactions]);
  };

  const handleCategorySuggested = (category: string) => {
    setFormData({ ...formData, category });
  };

  const getTransactionIcon = (type: 'income' | 'expense') => {
    return type === 'income' ? (
      <ArrowUpRight className="w-5 h-5 text-success-600" />
    ) : (
      <ArrowDownRight className="w-5 h-5 text-danger-600" />
    );
  };

  const getTransactionColor = (type: 'income' | 'expense') => {
    return type === 'income' ? 'text-success-600' : 'text-danger-600';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Transactions</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Track your income and expenses
          </p>
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 mt-4 sm:mt-0">
          <button
            onClick={() => setShowAddForm(true)}
            className="btn-primary flex items-center justify-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Transaction
          </button>
          <button
            onClick={() => setShowDocumentUpload(true)}
            className="btn-secondary flex items-center justify-center"
          >
            <FileText className="w-4 h-4 mr-2" />
            Upload Document
          </button>
          <button
            onClick={() => setShowBulkImport(true)}
            className="btn-secondary flex items-center justify-center"
          >
            <Upload className="w-4 h-4 mr-2" />
            Bulk Import
          </button>
        </div>
      </div>

      {/* Add Transaction Form */}
      {showAddForm && (
        <div className="card">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Add Transaction</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="input-field"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as 'income' | 'expense' })}
                  className="input-field"
                >
                  <option value="expense">Expense</option>
                  <option value="income">Income</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Category
              </label>
              <input
                type="text"
                required
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="input-field"
                placeholder="e.g., Food, Salary, Rent"
              />
              <AICategorySuggestion
                description={formData.description}
                onCategorySuggested={handleCategorySuggested}
              />
            </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="input-field"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-field"
                placeholder="Optional description"
              />
            </div>

            <div className="flex space-x-3">
              <button type="submit" className="btn-primary">
                Add Transaction
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Document Upload Modal */}
      {showDocumentUpload && (
        <DocumentUpload
          onTransactionsAdded={handleDocumentTransactionsAdded}
          onClose={() => setShowDocumentUpload(false)}
        />
      )}

      {/* Bulk Import Modal */}
      {showBulkImport && (
        <BulkTransactionImport
          onTransactionsAdded={handleBulkTransactionsAdded}
          onClose={() => setShowBulkImport(false)}
        />
      )}

      {/* AI Demo */}
      <AIDemo />

      {/* Transactions List */}
      <div className="card">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Recent Transactions</h2>
        <div className="space-y-4">
          {transactions.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">No transactions yet</p>
          ) : (
            transactions.map((transaction) => (
              <div
                key={transaction.id}
                className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    {getTransactionIcon(transaction.type)}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{transaction.category}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{transaction.description}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      {new Date(transaction.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-semibold ${getTransactionColor(transaction.type)}`}>
                    {formatCurrency(Math.abs(transaction.amount))}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}; 